import { c as _c } from "react/compiler-runtime";
import { usePortal } from 'components/portal/portal';
import { sb } from 'utils/db';
import { Feedback } from './feedback';
import { WorkspaceTypes as WT } from 'shared/forecast/workspaceTypes';
import { WorkspaceData as WD } from './workspaceData';
import { pick } from 'remeda';
import { z, ZodType } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { useQuerySbSync } from 'utils/db/useQuerySb';
import { DeltaValues, DeltaValuesDynamic } from 'shared/paths';
import { useDbForm } from 'components/hooks/useDbForm';
import { DateTime } from 'luxon';
import { mapEntries } from 'shared/collection';
import { useDebouncedFn } from 'components/hooks';
export namespace Workspace {
  export const {
    zCfg,
    zGlobalCfg,
    zDomainCfg,
    zDomainCfgNew,
    zInviteLinkCfg,
    zInviteLinkNew,
    zRequest,
    zRow,
    getZRow,
    cfgValidator,
    zGlobalAdmin,
    zGlobalAdminRow,
    GLOBAL_ID
  } = WT;
  export type Cfg = WT.Cfg;
  export type GlobalCfg = WT.GlobalCfg;
  export type UpgradeCfg = WT.UpgradeCfg;
  export type InviteLinkCfg = WT.InviteLinkCfg;
  export type Request = WT.Request;
  export type AdminExtra = WT.GlobalAdmin;
  export const useWorkspaces = () => {
    const $ = _c(5);
    const {
      profile,
      user
    } = usePortal();
    let t0;
    if ($[0] !== profile || $[1] !== user) {
      t0 = {
        mutationFn: async workspace => {
          if (!profile || !user) {
            throw new Error("no profile");
          }
          const res = await WD.createWorkspace(sb, {
            workspace,
            profile: pick(profile, ["name", "avatar_url", "tz"])
          });
          await sb.auth.refreshSession();
          await Feedback.createDefaultQuestionIfRequired(res);
          return res;
        }
      };
      $[0] = profile;
      $[1] = user;
      $[2] = t0;
    } else {
      t0 = $[2];
    }
    const create = useMutation(t0);
    let t1;
    if ($[3] !== create) {
      t1 = {
        create
      };
      $[3] = create;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    return t1;
  };
  export const useAdminWorkspace = <T extends Cfg,>(props?: {
    id?: string;
    enabled?: boolean;
    schema?: ZodType<T, any, any>;
    disableChannel?: boolean;
  }) => {
    const {
      profile,
      portalRefetch
    } = usePortal();
    const debouncedPortalRefetch = useDebouncedFn(portalRefetch, 1000);
    const id = props?.id ?? profile?.workspace_id;
    const schema = props?.schema ?? zCfg;
    const sbSync = useQuerySbSync({
      table: 'workspace',
      filter: {
        id
      },
      options: {
        enabled: !!id && props?.enabled !== false
      },
      schema: getZRow(schema),
      disableChannel: props?.disableChannel
    });
    const row = sbSync.data?.[0] as WT.Row<T> | undefined;
    const cfg = row ? schema.parse(row.cfg) as T : schema.parse({}) as T;
    const form = useDbForm({
      stored: cfg,
      onUpdate: async props_0 => {
        const delta = props_0.delta as DeltaValues<Cfg>;
        if (!id) return;
        // if delta is changing feedback then portalRefetch
        await sbSync.updateCfg({
          id: id!,
          cfg_updates: delta as any
        });
        if (delta.enableFeedback != undefined) {
          if (delta.enableFeedback == true) {
            // check if there is the default feedback and if not, create it
            await Feedback.createDefaultQuestionIfRequired({
              profile_id: profile.id,
              workspace_id: profile.workspace_id
            });
          }
        }
        debouncedPortalRefetch();
      },
      debounceSave: 1000,
      validator: cfgValidator
    });
    const del = async () => {
      if (!id) throw new Error('workspace_id is null');
      await sbSync.update({
        id,
        deleted: true
      }, {
        onSuccess: () => {
          window.location.href = '/';
        }
      });
    };
    const updateCfg = async (delta_0: DeltaValuesDynamic) => {
      if (!id) throw new Error('workspace_id is null');
      sbSync.updateCfg({
        id,
        cfg_updates: delta_0 as any
      });
    };
    const requests = mapEntries(cfg.requests, (key, request) => ({
      ...request,
      key
    }));
    const updateRequest = (key_0: string, r: Pick<Request, 'status'>) => {
      const request_0 = cfg.requests[key_0];
      if (!request_0) throw new Error(`request ${key_0} not found`);
      const newR = {
        ...request_0,
        ...r,
        updatedAt: DateTime.utc().toISO(),
        updatedBy: profile?.id!
      } satisfies Request;
      return updateCfg({
        [`requests.${key_0}`]: newR
      });
    };
    return {
      row,
      form,
      id,
      del,
      updateCfg,
      requests,
      updateRequest,
      isPending: sbSync.isPending || form.isDirty,
      refetch: sbSync.refetch,
      isError: sbSync.isError,
      error: sbSync.error
    };
  };
  export type UseAdminWorkspace = ReturnType<typeof useAdminWorkspace>;
  export const useAdminWorkspaceGlobal = props => {
    const $ = _c(2);
    const t0 = props?.enabled;
    let t1;
    if ($[0] !== t0) {
      t1 = {
        id: WT.GLOBAL_ID,
        enabled: t0,
        schema: WT.zGlobalCfg
      };
      $[0] = t0;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return useAdminWorkspace(t1);
  };
  export type UseAdminWorkspaceGlobal = ReturnType<typeof useAdminWorkspaceGlobal>;
}